import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createFeature, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { DocumentsActions } from '../../../../aumico-lib-annualreport-fe/src/lib/actions/workflow-documents.actions';
import { TasksOverviewActions } from '../actions/tasks.actions';
import { TASKS_FEATURE_KEY } from '../constants/constants';
import { DocumentDto } from '../dtos/document-dto.interface';
import { WorkflowArea } from '../enums/workflow-area.enum';
import { TaskMetaInformation } from '../interfaces/task-meta-information.interface';

export interface TasksOverviewState extends EntityState<TaskMetaInformation> {
    totalCount: number;
    limit: number;
    loading: boolean;
    error: string | null;
    documents: DocumentDto[];
}

// Entity adapter
export const taskMetadataAdapter = createEntityAdapter<TaskMetaInformation>({
    selectId: (task) => task.id,
});

// Initial state
export const initialTaskOverviewState: TasksOverviewState = taskMetadataAdapter.getInitialState({
    totalCount: 0,
    limit: 50,
    loading: false,
    error: null,
    documents: [],
});

export const selectFeature = createFeatureSelector<TasksOverviewState>(TASKS_FEATURE_KEY);

export const getDocuments = (state: TasksOverviewState) => state.documents;
export const selectDocuments = createSelector(selectFeature, getDocuments);

export const tasksReducer = createReducer(
    initialTaskOverviewState,

    // Update Document Metadata
    on(DocumentsActions.updateDocumentMetadataSuccess, (state, { document }) => {
        const { taskId } = document;
        if (!taskId) {
            return state;
        }

        const updatedDocuments = state.documents.map((doc) => (doc.id === document.id ? { ...doc, ...document } : doc));
        return {
            ...state,
            documents: updatedDocuments,
        };
    }),

    // Clear Active Tasks
    on(TasksOverviewActions.clearActiveTasks, (state) =>
        taskMetadataAdapter.removeAll({
            ...state,
            totalCount: 0,
            loading: false,
            error: null,
            documents: [],
        }),
    ),

    // Load Tasks Overview
    on(TasksOverviewActions.loadTasksOverview, (state) => ({ ...state, loading: true })),
    on(TasksOverviewActions.tasksOverviewLoaded, (state, { response }) =>
        taskMetadataAdapter.setAll(response.tasks, {
            ...state,
            totalCount: response.totalCount,
            loading: false,
            error: null,
        }),
    ),
    on(TasksOverviewActions.tasksOverviewLoadFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Create Task
    on(TasksOverviewActions.createTask, (state) => ({ ...state, loading: true })),
    on(TasksOverviewActions.taskCreated, (state, { response }) =>
        taskMetadataAdapter.addOne(response, { ...state, totalCount: state.totalCount + 1, loading: false }),
    ),
    on(TasksOverviewActions.taskCreateFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Update Task
    on(TasksOverviewActions.updateTask, (state) => ({ ...state, loading: true })),
    on(TasksOverviewActions.taskUpdated, (state, { response }) =>
        taskMetadataAdapter.updateOne({ id: response.id, changes: response }, { ...state, loading: false }),
    ),
    on(TasksOverviewActions.taskUpdateFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Update Task by Id
    on(TasksOverviewActions.updateTaskStatusForProjectByTaskId, (state) => ({ ...state, loading: true })),
    on(TasksOverviewActions.taskStatusForProjectByTaskIdUpdated, (state, { task }) =>
        taskMetadataAdapter.updateOne({ id: task.id, changes: task }, { ...state, loading: false }),
    ),
    on(TasksOverviewActions.taskStatusForProjectByTaskIdUpdateFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Bulk Update Tasks
    on(TasksOverviewActions.bulkUpdateTasks, (state) => ({ ...state, loading: true })),
    on(TasksOverviewActions.bulkTasksUpdated, (state, { response }) => {
        const updates = response.map((task) => ({
            id: task.id,
            changes: task,
        }));
        return taskMetadataAdapter.updateMany(updates, { ...state, loading: false });
    }),
    on(TasksOverviewActions.bulkUpdateTasksFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Update all tasks by section
    on(TasksOverviewActions.updateAllTasksStatusForProjectBySectionId, (state) => ({ ...state, loading: true })),
    on(TasksOverviewActions.allTasksStatusForProjectBySectionIdUpdated, (state, { tasks }) => {
        const updates = tasks.map((task) => ({
            id: task.id,
            changes: task,
        }));
        return taskMetadataAdapter.updateMany(updates, { ...state, loading: false });
    }),
    on(TasksOverviewActions.allTasksStatusForProjectBySectionIdUpdateFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Delete Task
    on(TasksOverviewActions.deleteTask, (state) => ({ ...state, loading: true })),
    on(TasksOverviewActions.taskDeleted, (state, { taskId }) =>
        taskMetadataAdapter.removeOne(taskId, { ...state, loading: false }),
    ),
    on(TasksOverviewActions.taskDeleteFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Bulk Delete Tasks
    on(TasksOverviewActions.bulkDeleteTasks, (state) => ({ ...state, loading: true })),
    on(TasksOverviewActions.bulkTasksDeleted, (state, { taskIds }) =>
        taskMetadataAdapter.removeMany(taskIds, { ...state, loading: false, totalCount: state.totalCount - taskIds.length }),
    ),

    // Load Project Tasks
    on(TasksOverviewActions.loadProjectTasks, (state) => ({ ...state, loading: true })),
    on(TasksOverviewActions.projectTasksLoaded, (state, { response }) =>
        taskMetadataAdapter.setAll(response.tasks, {
            ...state,
            documents: response.documents || [],
            totalCount: response.totalCount,
            loading: false,
            error: null,
        }),
    ),
    on(TasksOverviewActions.projectTasksLoadFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Reset state
    on(TasksOverviewActions.resetTasksOverviewState, (state) =>
        taskMetadataAdapter.removeAll({
            ...state,
            documents: [],
            totalCount: 0,
            loading: false,
            error: null,
        }),
    ),
);

export const tasksFeature = createFeature({
    name: 'aumicoTasksFeature',
    reducer: tasksReducer,
});

export const {
    selectAumicoTasksFeatureState,
    selectTotalCount,
    selectLimit,
    selectLoading,
    selectError,
} = tasksFeature;

const { selectAll, selectEntities } = taskMetadataAdapter.getSelectors();

export const selectAllTasks = createSelector(
    selectAumicoTasksFeatureState,
    selectAll,
);

export const selectTaskEntities = createSelector(selectAumicoTasksFeatureState, selectEntities);

export const selectTasksByProjectId = (projectId: string) =>
    createSelector(selectAllTasks, (all: TaskMetaInformation[]) => {
        return all.filter((task) => task.projectId === projectId);
    });

export const selectMandateManagementTasksByProjectId = (projectId: string) =>
    createSelector(selectAllTasks, (all: TaskMetaInformation[]) => {
        return all.filter((task) => task.projectId === projectId && task.area === WorkflowArea.MANDATE_MANAGEMENT);
    });

export const selectClosingTasksByProjectId = (projectId: string) =>
    createSelector(selectAllTasks, (all: TaskMetaInformation[]) => {
        return all.filter((task) => task.projectId === projectId && task.area === WorkflowArea.CLOSING_PROCESS);
    });

export const TasksFeature = createFeature({
    name: 'tasks',
    reducer: tasksReducer,
});

export function reducer(state: TasksOverviewState | undefined, action: Action) {
    return tasksReducer(state, action);
}
